import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as DocumentPicker from "expo-document-picker";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@louise/moon-ui";
import DocumentUploadModal, {
  type DocumentSlotInfo,
  type DocumentUploadSaveResult,
} from "@src/packages/document-vault/DocumentUploadModal";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  createDocumentProcessingFixture,
  DOCUMENT_ACTIVITY_FIXTURES,
  type DocumentActivityFixture,
  type DocumentProcessingItem,
} from "@src/packages/documents/ingestion/documentProcessingActivity";
import { getDocumentProcessingRunForUploadJobItem } from "@src/packages/documents/ingestion/documentProcessingRun";
import { countDocumentValuesToReview } from "@src/packages/documents/ingestion/documentValues";
import type {
  UploadJobSnapshot,
  UploadJobSourceContext,
} from "@src/packages/documents/ingestion";
import { useUploadJobActivity } from "@src/packages/documents/ingestion/useUploadJobActivity";
import { useApiClient } from "@src/packages/api/louise-api";
import { useTranslations } from "@src/packages/i18n";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";

type DocumentUploadRequest = {
  slot?: DocumentSlotInfo;
  defaultCategory?: string | null;
  defaultTags?: string[];
  ingestionContext?: UploadJobSourceContext;
  initialFile?: SelectedFile | null;
  initialFiles?: SelectedFile[] | null;
  onMonitoringExtracted?: (data: Record<string, unknown>) => void;
  onSave?: (result: DocumentUploadSaveResult) => void;
};

type DocumentUploadControllerValue = {
  openDocumentUpload: (request?: DocumentUploadRequest) => void;
  pickAndOpenDocumentUpload: (request?: DocumentUploadRequest) => Promise<void>;
  closeDocumentUpload: () => void;
  processingItems: DocumentProcessingItem[];
  updateProcessingItem: (
    id: string,
    update: Partial<DocumentProcessingItem>,
  ) => void;
  dismissProcessingItem: (id: string) => void;
};

function getDevActivityFixture(): DocumentActivityFixture | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const fixture = new URLSearchParams(window.location.search).get(
    "dev_documents_activity",
  );
  return DOCUMENT_ACTIVITY_FIXTURES.includes(fixture as DocumentActivityFixture)
    ? (fixture as DocumentActivityFixture)
    : null;
}

const AUTOMATIC_UPLOAD_ACTIVE_STATES = new Set([
  "selecting",
  "validating",
  "queued",
  "uploading",
  "uploaded",
  "processing",
  "background_processing",
  "saving",
]);

const AUTOMATIC_UPLOAD_COMPLETE_STATES = new Set([
  "complete",
  "source_only",
  "review_ready",
  "needs_review",
]);

function isReadyForAutomaticUploadCompletion(job: UploadJobSnapshot) {
  return (
    job.intent === "dump_anything" &&
    !job.acknowledgedAt &&
    job.items.length > 0 &&
    job.items.every(
      (item) =>
        !item.failure &&
        !AUTOMATIC_UPLOAD_ACTIVE_STATES.has(item.state) &&
        AUTOMATIC_UPLOAD_COMPLETE_STATES.has(item.state),
    )
  );
}

/** A finished upload whose values wait for the patient to confirm them. */
type DocumentWithValuesToReview = {
  patientDocumentId: string | null;
  name: string;
  count: number;
};

function documentNameFromFile(fileName: string): string {
  const base = fileName.trim().split(/[\\/]/).pop() ?? fileName.trim();
  return base.replace(/\.[^.]+$/, "") || fileName;
}

/**
 * What a finished upload tells the patient. Nothing a document says is saved
 * on its own, so a document with values invites the review that saves them;
 * one without says only that the file was saved.
 */
function completionToast(
  documents: readonly DocumentWithValuesToReview[],
  documentCount: number,
  t: ReturnType<typeof useTranslations>,
): { message: string; reviewDocumentId: string | null } | null {
  const withValues = documents.filter((document) => document.count > 0);
  const [only] = withValues;
  if (withValues.length === 1 && only) {
    return {
      message: t(
        only.count === 1
          ? "documents.processing_toast.values_found_in_document_one"
          : "documents.processing_toast.values_found_in_document_many",
        { params: { count: String(only.count), name: only.name } },
      ),
      reviewDocumentId: only.patientDocumentId,
    };
  }
  if (withValues.length > 1) {
    return {
      message: t("documents.processing_toast.values_found_in_documents", {
        params: { count: String(withValues.length) },
      }),
      reviewDocumentId: null,
    };
  }
  return documentCount > 0
    ? {
        message: t(
          documentCount === 1
            ? "documents.processing_toast.documents_saved_one"
            : "documents.processing_toast.documents_saved_many",
        ),
        reviewDocumentId: null,
      }
    : null;
}

const DocumentUploadControllerContext =
  createContext<DocumentUploadControllerValue | null>(null);

export function DocumentUploadControllerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const devFixture = getDevActivityFixture();
  const [request, setRequest] = useState<DocumentUploadRequest | null>(null);
  const [fixtureItems, setFixtureItems] = useState<DocumentProcessingItem[]>(
    () => (devFixture ? createDocumentProcessingFixture(devFixture) : []),
  );
  const { client, patientId } = useApiClient();
  const { showToast } = useToast();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const t = useTranslations();
  const completionNotifiedJobsRef = useRef(new Set<string>());
  const {
    jobs,
    processingItems: durableItems,
    cancelFile,
    retryFile,
    acknowledgeJob,
  } = useUploadJobActivity({ enabled: devFixture === null });
  const processingItems = devFixture ? fixtureItems : durableItems;

  useEffect(() => {
    if (devFixture || !patientId) return;

    for (const job of jobs) {
      if (
        !isReadyForAutomaticUploadCompletion(job) ||
        completionNotifiedJobsRef.current.has(job.id)
      ) {
        continue;
      }

      completionNotifiedJobsRef.current.add(job.id);
      void (async () => {
        const documents = (
          await Promise.all(
            job.items.map(
              async (item): Promise<DocumentWithValuesToReview | null> => {
                if (item.state === "source_only") return null;
                try {
                  const lookup = await getDocumentProcessingRunForUploadJobItem(
                    client,
                    patientId,
                    job.id,
                    item.id,
                  );
                  return {
                    patientDocumentId: lookup.patientDocumentId,
                    name: documentNameFromFile(item.name),
                    count: countDocumentValuesToReview(lookup.run),
                  };
                } catch (error) {
                  console.warn(
                    "Could not read what the finished document found",
                    error,
                  );
                  return null;
                }
              },
            ),
          )
        ).filter(
          (document): document is DocumentWithValuesToReview =>
            document !== null,
        );

        void queryClient.invalidateQueries({ queryKey: ["documents"] });
        const toast = completionToast(documents, job.items.length, t);
        if (toast) {
          const hasValues = documents.some((document) => document.count > 0);
          showToast({
            message: toast.message,
            variant: "success",
            ...(hasValues
              ? {
                  action: {
                    label: t("documents.processing_toast.review_values"),
                    analyticsAction: "review_document_values",
                    onPress: () =>
                      navigation.dispatch(
                        CommonActions.navigate("Home", {
                          screen: "Care",
                          params: {
                            screen: "Documents",
                            params: toast.reviewDocumentId
                              ? { initialDocumentId: toast.reviewDocumentId }
                              : {},
                          },
                        }),
                      ),
                  },
                }
              : {}),
          });
        }

        // The document itself holds what is left to review, so the finished
        // upload leaves the activity list either way.
        try {
          await acknowledgeJob(job.id);
        } catch (error) {
          console.warn("Could not dismiss completed document upload", error);
        }
      })();
    }
  }, [
    acknowledgeJob,
    client,
    devFixture,
    jobs,
    navigation,
    patientId,
    queryClient,
    showToast,
    t,
  ]);

  const closeDocumentUpload = useCallback(() => {
    setRequest(null);
  }, []);

  const updateProcessingItem = useCallback(
    (id: string, update: Partial<DocumentProcessingItem>) => {
      if (!devFixture) {
        const item = durableItems.find((candidate) => candidate.id === id);
        if (item?.durable && update.state === "processing") {
          void retryFile(item.fileItemId ?? item.id);
        }
        return;
      }

      setFixtureItems((current) =>
        current.map((item) => (item.id === id ? { ...item, ...update } : item)),
      );
    },
    [devFixture, durableItems, retryFile],
  );

  const dismissProcessingItem = useCallback(
    (id: string) => {
      if (!devFixture) {
        const item = durableItems.find((candidate) => candidate.id === id);
        if (!item?.durable) return;
        const isCancelable = [
          "selecting",
          "validating",
          "queued",
          "uploading",
        ].includes(item.state);
        if (isCancelable) {
          void cancelFile(item.fileItemId ?? item.id);
        } else if (item.jobId) {
          void acknowledgeJob(item.jobId);
        }
        return;
      }

      setFixtureItems((current) => current.filter((item) => item.id !== id));
    },
    [acknowledgeJob, cancelFile, devFixture, durableItems],
  );

  const openDocumentUpload = useCallback(
    (nextRequest: DocumentUploadRequest = {}) => {
      setRequest({
        slot: nextRequest.slot ?? null,
        defaultCategory: nextRequest.defaultCategory ?? null,
        defaultTags: nextRequest.defaultTags ?? [],
        ingestionContext: nextRequest.ingestionContext,
        initialFile: nextRequest.initialFile ?? null,
        initialFiles: nextRequest.initialFiles ?? null,
        onMonitoringExtracted: nextRequest.onMonitoringExtracted,
        onSave: nextRequest.onSave,
      });
    },
    [],
  );

  const pickAndOpenDocumentUpload = useCallback(
    async (nextRequest: DocumentUploadRequest = {}) => {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: ["application/pdf", "image/*"],
          copyToCacheDirectory: true,
          multiple: true,
        });

        if (result.canceled || !result.assets || result.assets.length === 0) {
          return;
        }

        openDocumentUpload({
          ...nextRequest,
          initialFiles: result.assets.map((file) => ({
            name: file.name,
            uri: file.uri,
            mimeType: resolveUploadContentType({
              name: file.name,
              uri: file.uri,
              mimeType: file.mimeType,
            }),
            size: file.size ?? undefined,
          })),
        });
      } catch (error) {
        console.error("Error picking document:", error);
      }
    },
    [openDocumentUpload],
  );

  const value = useMemo(
    () => ({
      openDocumentUpload,
      pickAndOpenDocumentUpload,
      closeDocumentUpload,
      processingItems,
      updateProcessingItem,
      dismissProcessingItem,
    }),
    [
      closeDocumentUpload,
      dismissProcessingItem,
      openDocumentUpload,
      pickAndOpenDocumentUpload,
      processingItems,
      updateProcessingItem,
    ],
  );

  return (
    <DocumentUploadControllerContext.Provider value={value}>
      {children}
      <DocumentUploadModal
        visible={request !== null}
        onClose={closeDocumentUpload}
        slot={request?.slot ?? null}
        defaultCategory={request?.defaultCategory ?? null}
        defaultTags={request?.defaultTags ?? []}
        ingestionContext={request?.ingestionContext}
        initialFile={request?.initialFile ?? null}
        initialFiles={request?.initialFiles ?? null}
        autoPickOnOpen={!visualEvidenceRoute}
        onMonitoringExtracted={request?.onMonitoringExtracted}
        onSave={request?.onSave}
      />
    </DocumentUploadControllerContext.Provider>
  );
}

export function useDocumentUploadController() {
  const context = useContext(DocumentUploadControllerContext);
  if (!context) {
    throw new Error(
      "useDocumentUploadController must be used inside DocumentUploadControllerProvider",
    );
  }
  return context;
}
